<template>
  <section class="map-management" aria-labelledby="map-management-title">
    <header class="map-management__header">
      <div><h1 id="map-management-title">地图管理</h1><p>查看平台地区数据，供地图组件配置使用。</p></div>
      <el-button size="small" icon="el-icon-refresh" :disabled="loading" aria-label="刷新地区列表" @click="getListData">刷新</el-button>
    </header>
    <p class="map-management__notice"><i class="el-icon-info" aria-hidden="true"></i>地区新增、编辑和删除请前往物联网平台地区管理。</p>
    <div class="map-management__meta" role="status" aria-live="polite">
      <span v-if="loading"><span class="ui-loading-spinner ui-loading-spinner--small" aria-hidden="true"></span>{{ data.length ? '正在刷新地区列表…' : '正在加载地区列表…' }}</span>
      <span v-else-if="!error">共 {{ data.length }} 个地区</span>
    </div>
    <div class="map-management__results" tabindex="0" role="region" aria-label="地区列表，可滚动浏览" :aria-busy="String(loading)">
      <div v-if="error" class="map-management__state" role="alert"><i class="el-icon-warning-outline" aria-hidden="true"></i><h2>地区列表加载失败</h2><p>{{ error }}</p><el-button size="small" @click="getListData">重新加载</el-button></div>
      <table v-else-if="loading || data.length" class="map-management__table">
        <thead><tr><th scope="col" class="map-management__index">序号</th><th scope="col">地区名称</th><th scope="col">地区全称</th><th scope="col">行政区划代码</th><th scope="col">说明</th></tr></thead>
        <tbody v-if="loading && !data.length" aria-hidden="true"><tr v-for="row in 6" :key="row"><td v-for="column in 5" :key="column"><span class="ui-skeleton"></span></td></tr></tbody>
        <tbody v-else><tr v-for="(row, index) in data" :key="row.id || row.code || index"><td>{{ index + 1 }}</td><th scope="row">{{ row.name || '—' }}</th><td>{{ row.fullName || '—' }}</td><td>{{ row.code || '—' }}</td><td>{{ row.description || '—' }}</td></tr></tbody>
      </table>
      <div v-else class="map-management__state" role="status"><i class="el-icon-location-outline" aria-hidden="true"></i><h2>暂无地区数据</h2><p>在物联网平台添加地区后，可刷新此列表查看。</p><el-button size="small" @click="getListData">刷新列表</el-button></div>
    </div>
  </section>
</template>

<script>
import { getList } from '@/api/map'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
export default {
  data: () => ({ data: [], loading: false, error: '', requestId: 0 }),
  created () { this.getListData() },
  beforeDestroy () { this.requestId++; if (this._read) this._read.cancel() },
  methods: {
    async getListData () {
      if (this.loading) return
      const requestId = ++this.requestId
      this.loading = true; this.error = ''
      const read = createRequestCancellation(); this._read = read
      try {
        const response = await getList({ cancelToken: read.token })
        if (requestId !== this.requestId) return
        const body = response && response.data
        if (!body || body.success === false || (body.status && body.status !== 200) || !Array.isArray(body.result)) throw new Error('地区列表数据无效，请刷新后重试')
        this.data = body.result
      } catch (error) {
        if (requestId === this.requestId && !isRequestCanceled(error)) this.error = error.message || '地区列表加载失败，请重试'
      } finally {
        if (requestId === this.requestId) { this.loading = false; this._read = null }
      }
    }
  },
};
</script>

<style scoped>
.map-management { display: flex; flex-direction: column; height: 100%; min-height: 0; padding: 24px; border: 1px solid var(--manage-border, #e5eaf1); border-radius: 8px; background: #fff; color: #1f2937; }
.map-management__header { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-shrink: 0; }
.map-management h1 { margin: 0; font-size: 20px; line-height: 28px; font-weight: 600; }
.map-management__header p { margin: 6px 0 0; color: #64748b; font-size: 13px; line-height: 1.6; }
.map-management__notice { flex-shrink: 0; margin: 20px 0 0; padding: 12px 14px; border-radius: 6px; background: #f1f6ff; color: #43618c; font-size: 13px; line-height: 1.7; }
.map-management__notice i { margin-right: 6px; }
.map-management__meta { min-height: 48px; padding: 14px 0; flex-shrink: 0; color: #64748b; font-size: 13px; }
.map-management__meta > span { display: flex; align-items: center; gap: 8px; }
.map-management__results { flex: 1; min-height: 0; overflow: auto; overscroll-behavior: contain; }
.map-management__table { border-collapse: collapse; width: 100%; min-width: 620px; table-layout: fixed; font-size: 13px; line-height: 1.7; }
.map-management__table th, .map-management__table td { padding: 14px 16px; text-align: left; border-bottom: 1px solid #edf0f5; font-weight: 400; overflow-wrap: anywhere; }
.map-management__table thead th { position: sticky; top: 0; background: #f6f8fb; color: #526178; font-weight: 500; }
.map-management__table .map-management__index { width: 72px; }
.map-management__table tbody tr:hover { background: #f7faff; }
.map-management__table .ui-skeleton { display: block; height: 14px; width: 75%; margin: 4px 0; border-radius: 3px; }
.map-management__state { display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; min-height: 100%; padding: 32px 16px; text-align: center; }
.map-management__state > i { font-size: 36px; color: #94a3b8; }
.map-management__state h2 { margin: 0; font-size: 16px; font-weight: 500; }
.map-management__state p { max-width: 440px; margin: 0; color: #64748b; font-size: 13px; line-height: 1.7; overflow-wrap: anywhere; }
@media (max-width: 700px) { .map-management { padding: 16px 12px; }.map-management__header { align-items: flex-start; }.map-management__header .el-button { min-height: 44px; flex-shrink: 0; } }
</style>
